import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord } from '@tailoring-crm/shared';
import { monthStartInputValue, todayInputValue } from '../../lib/attendance/attendance-day.js';
import { useAttendanceHistory } from '../../lib/attendance/use-attendance.js';
import { formatDate, formatMinutes, formatNumber, formatTime } from '../../lib/format.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ListSkeleton,
} from '@tailoring-crm/ui';
import { AttendanceStatusBadge } from './attendance-status-badge.js';

/** What an empty cell reads as. */
const NOTHING = '—';

const PAGE_SIZE = 31;

/**
 * One person's attendance over a range — the question a wage conversation actually starts from,
 * and the one the day search cannot be asked.
 *
 * IT COMPUTES NOTHING. The worked minutes and the counts of days present, late, half-day and on
 * leave are the totals the server sent for the whole range; adding up the page on screen would
 * report a fortnight as however much of it had loaded. Nothing is refused here that the contract
 * does not already refuse — a range that is backwards, or longer than a year, comes back from the
 * server as a message rather than being second-guessed in the browser.
 */
export function AttendanceHistoryDialog({
  record,
  onOpenChange,
}: {
  /** Whose history is open, or null when the dialog is closed. */
  record: AttendanceRecord | null;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  /** The current month, which is what somebody opening this is nearly always asking about. */
  const [from, setFrom] = useState(monthStartInputValue);
  const [to, setTo] = useState(todayInputValue);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const history = useAttendanceHistory(
    { staffUserId: record?.staffUserId ?? '', from, to, page: 1, pageSize },
    { enabled: record !== null },
  );

  const items = history.data?.items ?? [];
  const total = history.data?.total ?? 0;
  const totals = history.data?.totals;

  return (
    <Dialog open={record !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('attendance.history.title', { name: record?.staffName ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('attendance.history.hint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="attendance-history-from">{t('attendance.history.from')}</Label>
            <Input
              id="attendance-history-from"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPageSize(PAGE_SIZE);
              }}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="attendance-history-to">{t('attendance.history.to')}</Label>
            <Input
              id="attendance-history-to"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPageSize(PAGE_SIZE);
              }}
            />
          </div>
        </div>

        {history.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {history.error instanceof Error ? history.error.message : t('error.unexpected')}
          </p>
        ) : null}

        {totals ? (
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <HistoryTotal
              label={t('attendance.history.worked')}
              value={formatMinutes(totals.workedMinutes)}
            />
            <HistoryTotal
              label={t('attendance.history.present')}
              value={formatNumber(totals.present)}
            />
            <HistoryTotal label={t('attendance.history.late')} value={formatNumber(totals.late)} />
            <HistoryTotal
              label={t('attendance.history.halfDay')}
              value={formatNumber(totals.halfDay)}
            />
            <HistoryTotal
              label={t('attendance.history.onLeave')}
              value={formatNumber(totals.onLeave)}
            />
          </dl>
        ) : null}

        {history.isPending ? <ListSkeleton rows={3} /> : null}

        {!history.isPending && items.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            {t('attendance.history.empty')}
          </p>
        ) : null}

        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('attendance.history.day')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('attendance.column.status')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('attendance.column.checkIn')}
                  </th>
                  <th scope="col" className="py-1 pr-3 font-medium">
                    {t('attendance.column.checkOut')}
                  </th>
                  <th scope="col" className="py-1 font-medium">
                    {t('attendance.column.hours')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((day) => (
                  <tr key={day.id} className="border-t border-border" data-testid="history-row">
                    <th scope="row" className="py-1.5 pr-3 text-left font-normal tabular-nums">
                      {formatDate(`${day.day}T00:00:00.000Z`)}
                    </th>
                    <td className="py-1.5 pr-3">
                      <AttendanceStatusBadge
                        status={day.status}
                        late={day.late}
                        halfDay={day.halfDay}
                      />
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {day.checkInAt ? formatTime(day.checkInAt) : NOTHING}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {day.checkOutAt ? formatTime(day.checkOutAt) : NOTHING}
                    </td>
                    <td className="py-1.5 tabular-nums">{formatMinutes(day.workedMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {items.length > 0 ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t('attendance.history.showing', { count: items.length, total })}
            </span>
            {items.length < total ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageSize((current) => current + PAGE_SIZE)}
              >
                {t('attendance.loadMore')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function HistoryTotal({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded-md border border-border p-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

export default AttendanceHistoryDialog;
